import { z } from 'zod';

/**
 * A "strip view" of a schema: every `.strict()` object (catchall `never`) is
 * re-cloned in strip mode, deeply, so a response carrying keys this bundle
 * does not know about still parses (the extras are dropped). Needed because
 * shared wire schemas in `src/shared` are strict for the SERVER's own parse,
 * and a tab still running an old bundle must survive a deploy that adds a key.
 *
 * The original schema is never mutated, so server-side strictness is intact.
 * The view is the same schema class with the same checks, so the output type
 * stays `z.output<S>` (unlike `z.looseObject`, which would add an index
 * signature). Own property descriptors are copied as-is, so Zod's getter-based
 * defs (`default`/`prefault` `defaultValue`, recursive shapes) keep running per
 * access instead of being frozen into one shared value.
 *
 * Caveat: for a plain (non-discriminated) `z.union`, strip semantics mean the
 * first option that matches wins and drops extra keys, unlike strict options
 * that would have rejected them and fallen through to a later option.
 *
 * A strip view tolerates keys the server ADDED, never keys it REMOVED — see the
 * wire-compat rule on `SignalRowSchema` (`src/shared/flow/signal-view.ts`).
 *
 * Only a `never` catchall is cleared; a deliberate `z.catchall(z.string())`
 * or `z.looseObject` is left as authored.
 */

type AnySchema = z.core.$ZodType;
// `clone` is on every `ZodType`; `$ZodType` itself does not declare it.
type CloneableSchema = AnySchema & { clone(def?: unknown): AnySchema };

const viewCache = new WeakMap<object, AnySchema>();

function defOf(schema: AnySchema): Record<string, unknown> {
  return schema._zod.def as unknown as Record<string, unknown>;
}

/** Returns a copy of `def` whose listed keys are replaced; `lazy` keys are
 *  resolved on first read so recursive (getter-based) schemas never loop. */
function withDef(
  def: Record<string, unknown>,
  lazyKeys: Record<string, () => unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const key of Object.keys(def)) {
    if (key in lazyKeys) continue;
    Object.defineProperty(next, key, Object.getOwnPropertyDescriptor(def, key)!);
  }
  for (const [key, make] of Object.entries(lazyKeys)) {
    let cached: { value: unknown } | undefined;
    Object.defineProperty(next, key, {
      enumerable: true,
      configurable: true,
      get() {
        cached ??= { value: make() };
        return cached.value;
      },
    });
  }
  return next;
}

const view = (schema: unknown): AnySchema => toStripView(schema as AnySchema);

function build(schema: AnySchema): AnySchema {
  const def = defOf(schema);
  const clone = (next: Record<string, unknown>): AnySchema => (schema as CloneableSchema).clone(next);

  switch (def.type) {
    case 'object': {
      const shape = def.shape as Record<string, AnySchema>;
      const catchall = def.catchall as AnySchema | undefined;
      const strict = catchall !== undefined && defOf(catchall).type === 'never';
      return clone(
        withDef(def, {
          shape: () => Object.fromEntries(Object.entries(shape).map(([k, v]) => [k, view(v)])),
          catchall: () => (strict ? undefined : catchall && view(catchall)),
        })
      );
    }
    case 'array':
      return clone(withDef(def, { element: () => view(def.element) }));
    case 'set':
      return clone(withDef(def, { valueType: () => view(def.valueType) }));
    case 'optional':
    case 'nullable':
    case 'default':
    case 'prefault':
    case 'nonoptional':
    case 'readonly':
    case 'catch':
    case 'success':
    case 'promise':
      return clone(withDef(def, { innerType: () => view(def.innerType) }));
    case 'union':
      return clone(withDef(def, { options: () => (def.options as AnySchema[]).map(view) }));
    case 'pipe':
      return clone(withDef(def, { in: () => view(def.in), out: () => view(def.out) }));
    case 'lazy':
      return clone(withDef(def, { getter: () => () => view((def.getter as () => AnySchema)()) }));
    case 'record':
    case 'map':
      return clone(
        withDef(def, {
          keyType: () => view(def.keyType),
          valueType: () => view(def.valueType),
        })
      );
    case 'tuple':
      return clone(
        withDef(def, {
          items: () => (def.items as AnySchema[]).map(view),
          rest: () => (def.rest ? view(def.rest) : def.rest),
        })
      );
    case 'intersection':
      return clone(withDef(def, { left: () => view(def.left), right: () => view(def.right) }));
    default:
      return schema;
  }
}

export function toStripView<S extends z.ZodType>(schema: S): S;
export function toStripView(schema: AnySchema): AnySchema;
export function toStripView(schema: AnySchema): AnySchema {
  const cached = viewCache.get(schema);
  if (cached) return cached;
  // Seed before recursing is impossible (the clone needs the walk's result),
  // but every recursive edge goes through a lazy getter above, so the cache
  // entry below exists by the time a cycle re-enters.
  const built = build(schema);
  viewCache.set(schema, built);
  return built;
}
